/**
 * AIS target state and the source selector, mirroring the chart/tide/weather
 * multi-source pattern and the GNSS position-source seam. Targets are keyed by
 * MMSI and updated in place. The SOURCE chooses where they come from:
 *   - 'sim'      : synthetic training vessels (works over the fictional area).
 *   - 'internet' : live online AIS (AISStream.io WebSocket) for the real area.
 *   - 'nmea'     : a real connected receiver via AIVDM/AIVDO sentences.
 * Real sources only carry vessels where real ships are (so training uses sim).
 */
import { create } from 'zustand';
import { appendTrailPoint } from '@/ais/trails';

export type AisConnState = 'idle' | 'nokey' | 'connecting' | 'live' | 'stalled' | 'error';

/**
 * A plain-English status line for the live AIS feed, so "no ships" is never a
 * silent mystery: the skipper can see whether we're connecting, connected with
 * targets, connected but with none in range (genuinely quiet water — AISStream is
 * shore-based, so a remote loch really can be empty), missing a key, or dropped.
 * Pure so it can be unit-tested and reused wherever the status is shown.
 */
export function aisStatusLabel(state: AisConnState, targetCount: number, msg = ''): string {
  switch (state) {
    case 'connecting':
      return 'Connecting to the online AIS feed…';
    case 'live':
      return targetCount > 0
        ? `Connected — ${targetCount} target${targetCount === 1 ? '' : 's'} in range`
        : 'Connected — no targets in range yet';
    case 'stalled':
      // The socket is up but no frame has arrived for a while yet we still hold
      // targets, so those contacts are all ageing toward "lost" together. Say the
      // FEED is the problem, not that the traffic vanished, and that we're on it.
      return 'AIS feed lost — reconnecting…';
    case 'nokey':
      return 'No AISStream key — enter your AISStream key in the field below to use the online feed';
    case 'error':
      return msg ? `Disconnected — ${msg}` : 'Disconnected — retrying';
    case 'idle':
    default:
      return 'Online AIS is off';
  }
}

const KEY_LS = 'acp.ais.v1';
function loadKey(): string {
  try {
    return (typeof localStorage !== 'undefined' ? localStorage.getItem(KEY_LS) : null) ?? '';
  } catch {
    return '';
  }
}

const VEC_LS = 'acp.ais.vectorMin.v1';
/** Course/speed vector time-ahead (minutes); 6 is the usual ECDIS default. */
function loadVectorMin(): number {
  try {
    const v = typeof localStorage !== 'undefined' ? localStorage.getItem(VEC_LS) : null;
    const n = v != null ? Number(v) : NaN;
    return Number.isFinite(n) && n > 0 ? n : 6;
  } catch {
    return 6;
  }
}

const EN_LS = 'acp.ais.enabled.v1';
/** Whether AIS targets are shown; persisted so it survives an app kill/restart. */
function loadEnabled(): boolean {
  try {
    return (typeof localStorage !== 'undefined' ? localStorage.getItem(EN_LS) : null) === '1';
  } catch {
    return false;
  }
}

export type AisSourceId = 'sim' | 'internet' | 'nmea';

export interface AisTarget {
  mmsi: number;
  name?: string;
  shipType?: string;
  /**
   * Raw AIS ship-type code (0..99) from a static-data frame, kept alongside the
   * human `shipType` label. The chart symbology colours/shapes a target from this
   * numeric code when we have it (it's the canonical value), so we store it rather
   * than reverse-engineering a category back out of the label string.
   */
  shipTypeCode?: number;
  /** Overall length (m) from AIS dimensions (to-bow + to-stern), if reported. */
  lengthM?: number;
  /** Overall beam (m) from AIS dimensions (to-port + to-starboard), if reported. */
  beamM?: number;
  /** [lon, lat]. */
  position: [number, number];
  /** Course over ground, degrees true. */
  cogDeg: number;
  /** Speed over ground, knots. */
  sogKn: number;
  /** Heading, degrees true (falls back to COG for the symbol if absent). */
  headingDeg?: number;
  /** Last update (UTC ms), for staleness. */
  lastMs: number;
}

interface AisState {
  source: AisSourceId;
  setSource: (s: AisSourceId) => void;
  /** Show AIS targets on the chart. */
  enabled: boolean;
  setEnabled: (b: boolean) => void;
  targets: Record<number, AisTarget>;
  /** Recent track points per target (mmsi -> trail), for the history tail. */
  trails: Record<number, [number, number][]>;
  upsert: (t: AisTarget) => void;
  upsertMany: (ts: AisTarget[]) => void;
  /** Drop targets not heard from since `cutoffMs`. */
  prune: (cutoffMs: number) => void;
  clear: () => void;
  /** The tapped target's MMSI, for the detail sheet. */
  selectedMmsi: number | null;
  select: (m: number | null) => void;

  /** Live source connection state (for the internet/NMEA sources). */
  connState: AisConnState;
  connMsg: string;
  setConn: (state: AisConnState, msg?: string) => void;

  /** AISStream API key entered in Settings (overrides the .env.local key). */
  apiKey: string;
  setApiKey: (k: string) => void;

  /** Course/speed vector time-ahead, minutes (default 6, ECDIS convention). */
  vectorMin: number;
  setVectorMin: (m: number) => void;
}

export const useAisStore = create<AisState>((set) => ({
  source: 'sim',
  setSource: (source) => set({ source, targets: {}, trails: {}, selectedMmsi: null, connState: 'idle', connMsg: '' }),
  enabled: loadEnabled(),
  setEnabled: (enabled) => {
    try {
      if (typeof localStorage !== 'undefined') localStorage.setItem(EN_LS, enabled ? '1' : '0');
    } catch {
      /* ignore */
    }
    set({ enabled });
  },
  targets: {},
  trails: {},
  upsert: (t) =>
    set((s) => ({
      targets: { ...s.targets, [t.mmsi]: t },
      trails: { ...s.trails, [t.mmsi]: appendTrailPoint(s.trails[t.mmsi], t.position) },
    })),
  upsertMany: (ts) =>
    set((s) => {
      const next = { ...s.targets };
      const trails = { ...s.trails };
      for (const t of ts) {
        next[t.mmsi] = t;
        trails[t.mmsi] = appendTrailPoint(trails[t.mmsi], t.position);
      }
      return { targets: next, trails };
    }),
  prune: (cutoffMs) =>
    set((s) => {
      const next: Record<number, AisTarget> = {};
      const trails: Record<number, [number, number][]> = {};
      for (const [k, v] of Object.entries(s.targets)) {
        if (v.lastMs >= cutoffMs) {
          next[Number(k)] = v;
          if (s.trails[Number(k)]) trails[Number(k)] = s.trails[Number(k)];
        }
      }
      return { targets: next, trails };
    }),
  clear: () => set({ targets: {}, trails: {}, selectedMmsi: null }),
  selectedMmsi: null,
  select: (selectedMmsi) => set({ selectedMmsi }),

  connState: 'idle',
  connMsg: '',
  setConn: (connState, connMsg = '') => set({ connState, connMsg }),

  apiKey: loadKey(),
  setApiKey: (apiKey) => {
    try {
      if (typeof localStorage !== 'undefined') localStorage.setItem(KEY_LS, apiKey);
    } catch {
      /* ignore */
    }
    set({ apiKey });
  },

  vectorMin: loadVectorMin(),
  setVectorMin: (vectorMin) => {
    try {
      if (typeof localStorage !== 'undefined') localStorage.setItem(VEC_LS, String(vectorMin));
    } catch {
      /* ignore */
    }
    set({ vectorMin });
  },
}));
